import React, {
  createContext, useState, useEffect, useCallback, useMemo,
} from 'react';

interface Phone {
  displayName: string;
}
interface CompareToolBarContextI {
  selectedPhones: Array<Phone>;
  addPhone: (newPhone?: Phone) => void;
  removePhone: (newPhone?: Phone) => void;
  removeAllPhones: () => void;
}
const defaultContextValue: CompareToolBarContextI = {
  selectedPhones: [],
  addPhone: () => null,
  removePhone: () => null,
  removeAllPhones: () => null,
};

const CompareToolBarContext = createContext(defaultContextValue);

const ContextProviderComponent = ({ children }) => {
  const [selectedPhones, setSelectedPhones] = useState(defaultContextValue.selectedPhones);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const phones = JSON.parse(window.sessionStorage.getItem('phoneComparison') as string);
      if (phones) {
        setSelectedPhones(phones);
      }
    }
  }, []);

  const addPhone = useCallback((newPhone) => {
    const newSelectedPhones = [...selectedPhones, newPhone];
    setSelectedPhones(newSelectedPhones);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('phoneComparison', JSON.stringify(newSelectedPhones));
    }
  }, [selectedPhones]);

  const removePhone = useCallback((newPhone) => {
    const newSelectedPhones = selectedPhones.filter(
      (oldPhone) => oldPhone.displayName !== newPhone.displayName,
    );
    setSelectedPhones(newSelectedPhones);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('phoneComparison', JSON.stringify(newSelectedPhones));
    }
  }, [selectedPhones]);

  const removeAllPhones = () => {
    setSelectedPhones([]);
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('phoneComparison');
    }
  };

  const contextValue = useMemo(() => ({
    ...defaultContextValue,
    selectedPhones,
    addPhone,
    removePhone,
    removeAllPhones,
  }), [addPhone, removePhone, selectedPhones]);

  return (
    <CompareToolBarContext.Provider value={contextValue}>
      {children}
    </CompareToolBarContext.Provider>
  );
};

export default CompareToolBarContext.Consumer;
export { ContextProviderComponent, CompareToolBarContext };
